import React from 'react';
import type { AddressType, IconName } from '@sweetgreen/garnish';
import {
  AddressTypes,
  ListAction,
  ListActionPressable,
  switchcase,
} from '@sweetgreen/garnish';

import type { PartialRestaurant } from '../../graphql/partials';
import { useActiveRouteName } from '../../navigation';
import { useChangeLocation } from '../../screens/ReorderingScreen/hooks';
import { useShouldFinishReorderInMenu } from '../../screens/ReorderingScreen/ReorderingScreen.navigation';

export const RestaurantListAction = ({
  orderId,
  orderType,
  restaurant,
  canChangeLocation = () => true,
}: RestaurantListActionProps) => {
  const isOnMenuScreens = useShouldFinishReorderInMenu();
  const isOnCheckout = useActiveRouteName() === 'Checkout';
  const changeLocation = useChangeLocation();

  const {
    id: restaurantId,
    slug: restaurantSlug,
    name: restaurantName,
  } = restaurant ?? {};

  const handlePress = React.useCallback(() => {
    if (!canChangeLocation()) return;
    changeLocation({
      remainOnCheckout: isOnCheckout,
      finishOnMenu: isOnMenuScreens,
      orderId,
      orderType,
      deliveryOrderDetail: undefined,
      restaurantId,
      restaurantSlug,
      restaurantName,
      userInitiated: true,
    });
  }, [
    canChangeLocation,
    changeLocation,
    isOnMenuScreens,
    isOnCheckout,
    orderId,
    orderType,
    restaurantId,
    restaurantSlug,
    restaurantName,
  ]);

  return (
    <ListActionPressable
      iconName={getIconName(orderType)}
      title={restaurant?.name}
      onPress={handlePress}
    >
      <ListAction.Text>{restaurant?.address}</ListAction.Text>
      <ListAction.Text>
        {restaurant?.city}, {restaurant?.state} {restaurant?.zipCode}
      </ListAction.Text>
    </ListActionPressable>
  );
};

const getIconName = (orderType: AddressType): IconName => {
  return switchcase({
    [AddressTypes.pickup]: 'IconLocationPin',
    [AddressTypes.outpost]: 'IconLocationPin',
    [AddressTypes.delivery]: 'IconSweetgreen',
  })('IconSweetgreen')(orderType) as IconName;
};

// ––– TYPES ––––––––––––––––––––––––––––––––––––––––––––––––––––

type RestaurantListActionProps = {
  readonly orderId?: string;
  readonly orderType: AddressType;
  readonly restaurant?: Partial<PartialRestaurant>;
  readonly canChangeLocation?: () => boolean;
};
